import React, {
  FC,
  forwardRef,
  HTMLInputTypeAttribute,
  Ref,
  useCallback,
  useEffect,
} from "react";
import StyledDialog from "@components/StyledDialog";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Grid2 as Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CustomLabelInput from "@components/CustomLabelInput";
import { Controller, useForm } from "react-hook-form";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import {
  useCreateInvitationMutation,
  useGetInvitationQuery,
} from "@store/invitationApi";
import { setMessage } from "@store/snackbarSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { InviteType } from "@models/invitationRequest";
import { setOpen } from "@store/inviteSlice";

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const CustomPhoneInput = forwardRef(
  (props: any, ref: Ref<HTMLInputElement>) => {
    return (
      <TextField
        size="small"
        variant="outlined"
        fullWidth
        {...props}
        inputRef={ref}
      />
    );
  },
);

const Section: FC<{
  id?: string;
  name: string;
  title: string;
  label: string;
  placeholder?: string;
  buttonText: string;
  onSubmit: (value: string) => void;
  readOnly?: boolean;
  disabled?: boolean;
  value?: string;
  required?: boolean;
  inputType?: HTMLInputTypeAttribute;
  validators?: any;
}> = ({
  id,
  name,
  title,
  label,
  placeholder,
  buttonText,
  onSubmit,
  readOnly = false,
  disabled = false,
  value,
  required = true,
  inputType,
  validators,
}) => {
  const {
    register,
    reset,
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
  } = useForm();

  useEffect(() => {
    reset({ [name]: value });
  }, [value, reset, name]);

  return (
    <Stack marginTop="24px">
      <Typography
        fontSize="20px"
        lineHeight="30px"
        fontWeight="600"
        color="#565D6DFF"
        marginBottom="8px"
      >
        {title}
      </Typography>

      <form
        onSubmit={(e) => {
          return handleSubmit((v) => onSubmit(v[name]))(e);
        }}
      >
        <Grid container spacing={3}>
          <Grid size={9}>
            <CustomLabelInput label={label} id={id}>
              {inputType === "tel" ? (
                <Controller
                  name={name}
                  control={control}
                  rules={{
                    required,
                    validate: isValidPhoneNumber,
                    ...validators,
                  }}
                  render={({ field }) => (
                    <PhoneInput
                      {...field}
                      placeholder={placeholder}
                      defaultCountry="US"
                      id={id}
                      inputComponent={CustomPhoneInput}
                    />
                  )}
                />
              ) : (
                <TextField
                  {...register(name, {
                    required,
                    ...validators,
                  })}
                  type="text"
                  size="small"
                  variant="outlined"
                  fullWidth
                  placeholder={placeholder}
                  disabled={disabled}
                  slotProps={{
                    htmlInput: { readOnly, type: inputType },
                  }}
                />
              )}
            </CustomLabelInput>
          </Grid>

          <Grid size={3} alignSelf="flex-end">
            <Button
              disabled={!isValid || isSubmitting}
              type="submit"
              fullWidth
              variant="outlined"
              sx={{ marginBottom: "2px" }}
            >
              {buttonText}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Stack>
  );
};

const InviteDialog: FC = () => {
  const open = useSelector((state: RootState) => state.invite.open);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const [sendInvitation] = useCreateInvitationMutation();
  const { data: invitation } = useGetInvitationQuery(
    { patientId: selectedPatientId! },
    { skip: !selectedPatientId || !open },
  );

  const copyToClipboard = useCallback(() => {
    if (!invitation) {
      return;
    }
    navigator.clipboard.writeText(invitation.inviteLink);
    dispatch(
      setMessage({
        content: t(`dashboard.inviteDialog.copySuccessMessage`),
        severity: "success",
      }),
    );
  }, [invitation, dispatch, t]);

  const onClose = useCallback(() => {
    dispatch(setOpen(false));
  }, [dispatch]);

  const submit = useCallback(
    async ({
      email,
      phoneNumber,
    }: {
      email?: string;
      phoneNumber?: string;
    }) => {
      if (!selectedPatientId || (!email && !phoneNumber)) {
        return;
      }
      const { error } = await sendInvitation({
        patientId: selectedPatientId,
        email,
        phoneNumber,
        type: phoneNumber ? InviteType.Sms : InviteType.Email,
      });

      if (error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      } else {
        dispatch(
          setMessage({
            content: t(`dashboard.inviteDialog.sendSuccessMessage`),
            severity: "success",
          }),
        );
        onClose();
      }
    },
    [dispatch, t, sendInvitation, onClose, selectedPatientId],
  );

  return (
    <StyledDialog
      maxWidth="xs"
      open={open}
      onClose={onClose}
      title={t("dashboard.inviteDialog.title")}
    >
      <Stack>
        <Alert severity="info">
          {t("dashboard.inviteDialog.infoLabel", {
            appTitle: process.env.REACT_APP_APP_NAME,
          })}
        </Alert>

        <Section
          id="inviteDialogEmail"
          name="email"
          title={t("dashboard.inviteDialog.sendByEmail.title")}
          label={t("dashboard.inviteDialog.sendByEmail.input.label")}
          placeholder={t(
            "dashboard.inviteDialog.sendByEmail.input.placeholder",
          )}
          buttonText={t("dashboard.inviteDialog.sendByEmail.button")}
          onSubmit={(email) => submit({ email })}
          validators={{
            pattern: {
              value: EMAIL_PATTERN,
              message: "",
            },
          }}
        />

        <Section
          id="inviteDialogPhoneNumber"
          name="phoneNumber"
          title={t("dashboard.inviteDialog.sendByText.title")}
          label={t("dashboard.inviteDialog.sendByText.input.label")}
          placeholder={t("dashboard.inviteDialog.sendByText.input.placeholder")}
          buttonText={t("dashboard.inviteDialog.sendByText.button")}
          onSubmit={(phoneNumber) => submit({ phoneNumber })}
          inputType="tel"
        />

        <Section
          name="inviteLink"
          title={t("dashboard.inviteDialog.getLink.title")}
          label={t("dashboard.inviteDialog.getLink.input.label")}
          buttonText={t("dashboard.inviteDialog.getLink.button")}
          onSubmit={copyToClipboard}
          readOnly={true}
          disabled={true}
          required={false}
          value={invitation?.inviteLink || ""}
        />
      </Stack>
    </StyledDialog>
  );
};

export default InviteDialog;
